import { faBuilding } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  DialogContent, Grid, TextField, Typography
} from '@mui/material';
import { FormAddress, FormCountryIdentifier } from 'components';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { UserHelper, translate } from 'utils';

import { CompanyService, CountryService } from 'services';
import { CustomActionButtons } from './_CustomActionButtons';
import { HeaderModal } from './_HeaderModal';

export const CreateInstitutionModal = ({
                                         onConfirm, onClose, closeOnSubmit, defaultValues, parentInstitutionGroupId, parentCompanyId
                                       }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isPublic, setIsPublic] = useState(false);
  const [identifierFields, setIdentifierFields] = useState([]);
  const {
    handleSubmit,
    setValue,
    watch,
    formState: { errors, touchedFields }
  } = useForm({
    defaultValues: {
      ...defaultValues,
      activated: defaultValues.activated !== null ? defaultValues.activated : true,
      attestationNumber: defaultValues.attestationNumber || '',
      address1: (defaultValues.address && defaultValues.address.address1) || '',
      address2: (defaultValues.address && defaultValues.address.address2) || '',
      postalCode: (defaultValues.address && defaultValues.address.postalCode) || '',
      city: (defaultValues.address && defaultValues.address.city) || '',
      automaticCerfaDelivery: (defaultValues.automaticCerfaDelivery) || false,
      institutionName: defaultValues.name || '',
      country: null,
      legalStatus: null
    }
  });

  const formState = {
    values: watch(),
    touched: touchedFields,
    errors,
    setField: (name, value) => setValue(name, value, { shouldValidate: true, shouldDirty: true, shouldTouch: true }),
    clearField: (name) => setValue(name, '', { shouldValidate: true, shouldDirty: true, shouldTouch: true })
  };

  const text = (name) => ({
    name,
    value: watch(name) || '',
    onChange: (e) => setValue(name, e.target.value, { shouldValidate: true, shouldDirty: true, shouldTouch: true })
  });

  const raw = (name) => ({
    name,
    value: watch(name),
    onChange: (value) => setValue(name, value, { shouldValidate: true, shouldDirty: true, shouldTouch: true })
  });

  useEffect(() => {
    if (defaultValues.id) {
      setIsPublic(defaultValues.isPublic);
    } else {
      CompanyService.isPublic(parentCompanyId)
          .then(setIsPublic);
    }
    // eslint-disable-next-line
  }, [parentCompanyId, defaultValues]);

  useEffect(() => {
    const defaultCountry = UserHelper.getDefaultCountry(defaultValues.address);
    CountryService.getCountrySelectItems()
        .then(countries => {
          formState.setField('country', countries.find(country => country.value?.trim() === defaultCountry.value?.trim()));
        });
    // eslint-disable-next-line
  }, [defaultValues.address]);

  const onSubmit = useCallback(data => {
    setIsLoading(true);

    const newInstitution = {
      ...data,
      identifiers: identifierFields,
      id: defaultValues.id || null,
      name: data.institutionName || '',
      address: {
        hashId: (defaultValues.address && defaultValues.address.hashId) || null,
        address1: data.address1,
        address2: data.address2,
        postalCode: data.postalCode,
        city: data.city,
        country: {
          ...data.country,
          key: data.country && data.country.countryCode
        }
      },
      parentInstitutionGroupId,
      parentCompanyId
    };

    onConfirm(newInstitution)
        .finally(() => setIsLoading(false))
        .then(() => {
          if (closeOnSubmit) {
            onClose();
          }
        })
        .catch(() => {

        });
  }, [closeOnSubmit, onClose, onConfirm, defaultValues, parentInstitutionGroupId, parentCompanyId, identifierFields]);

  const handleValidateModal = handleSubmit(onSubmit);

  return (
    <form autoComplete="off" name="institutionForm" onSubmit={handleValidateModal}>
      <HeaderModal onClose={onClose}>
        <FontAwesomeIcon icon={faBuilding} />
        <Typography component="span">
          {defaultValues && defaultValues.name
                ? translate('modalInstitution.editInstitution')
                : translate('modalInstitution.newInstitution')}
        </Typography>
      </HeaderModal>

      <DialogContent style={{ minWidth: '650px', paddingTop: '10px' }}>
        <Grid container direction="column">
          <FormCountryIdentifier
                columnSize={12}
                formState={formState}
                hasDefaultSiret={defaultValues.siret}
                identifierFields={identifierFields}
                isPublic={isPublic}
                raw={raw}
                setIdentifierFields={setIdentifierFields}
                text={text}
            />

          <TextField
                autoComplete="new-password"
                inputProps={{ maxLength: 32 }}
                label={translate('common.attestationNumber')}
                name="attestationNumber"
                {...text('attestationNumber')}
            />

          <div style={{ margin: '1rem 0' }} />

          <FormAddress
                formState={formState}
                isRequired
                text={text}
                withCountry={false}
            />
        </Grid>
      </DialogContent>

      <CustomActionButtons
            isLoading={isLoading}
            isValidated={defaultValues.validated}
            onClose={onClose}
        />
    </form>
  );
};

CreateInstitutionModal.propTypes = {
  onConfirm: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  closeOnSubmit: PropTypes.bool,
  defaultValues: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    isPublic: PropTypes.bool,
    activated: PropTypes.bool,
    attestationNumber: PropTypes.string,
    automaticCerfaDelivery: PropTypes.bool,
    validated: PropTypes.bool,
    siret: PropTypes.string,
    address: PropTypes.shape({
      hashId: PropTypes.string,
      address1: PropTypes.string,
      address2: PropTypes.string,
      postalCode: PropTypes.string,
      city: PropTypes.string
    })
  }),
  parentInstitutionGroupId: PropTypes.string,
  parentCompanyId: PropTypes.string
};

CreateInstitutionModal.defaultProps = {
  closeOnSubmit: true,
  defaultValues: {},
  parentInstitutionGroupId: null,
  parentCompanyId: null
};
